import type { Theme } from '@mui/material';
import {
  Button,
  Card,
  CardHeader,
  Divider,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { PropertyList } from '../../../../../components/atoms/property-list';
import { PropertyListItem } from '../../../../../components/atoms/property-list-item';
import AssignHostsToDestinationDialog from '../../organisms/dialogs/assign-hosts-to-destination-dialog';

import { useDialog } from '@/hooks/use-dialog';
import { Can } from '@/permissions/can';
import { getLabel } from '@/utils/get-label';

interface DestinationHostsProps {
  id: number;
  hosts: { id: number; firstName: string; lastName: string; email: string }[];
}

export const DestinationHosts: FC<DestinationHostsProps> = props => {
  const { id, hosts, ...other } = props;
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const align = mdDown ? 'vertical' : 'horizontal';
  const { t } = useTranslation(['destination', 'common']);

  const [isOpenAssignHostsDialog, handleOpenAssignHostsDialog, handleCloseAssignHostsDialog] =
    useDialog();

  return (
    <>
      <Card variant="outlined" {...other}>
        <CardHeader
          title={t('destination:hosts.cardTitle')}
          action={
            <Can I="manageDestinations" a="Hosts" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      onClick={handleOpenAssignHostsDialog}
                      color="primary"
                      variant="text"
                      size="large"
                      disabled={!allowed}>
                      {t('common:button.edit')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can>
          }
        />
        <Divider />
        <PropertyList>
          {hosts.length > 0 ? (
            hosts.map(host => {
              const label = getLabel({
                values: [host.firstName, host.lastName],
                fallback: '-',
              });

              return (
                <PropertyListItem key={host.id} align={align} label={label} width={200}>
                  <Typography
                    color="textSecondary"
                    variant="body2"
                    sx={{ wordBreak: 'break-word' }}>
                    {host.email}
                  </Typography>
                </PropertyListItem>
              );
            })
          ) : (
            <Typography sx={{ p: 3 }} variant="inherit">
              {t('destination:hosts.noHosts')}
            </Typography>
          )}
        </PropertyList>
      </Card>

      <AssignHostsToDestinationDialog
        open={isOpenAssignHostsDialog}
        onCancel={handleCloseAssignHostsDialog}
        destinationId={id}
        hostsIds={hosts.map(host => host.id)}
      />
    </>
  );
};
